<script setup>
import {
  ref,
  useSanity,
  useEventTracking,
  computed,
  useSanityQuery,
  useAsyncData,
} from '#imports';
import { getQuery as getArticlesQuery } from '~/groq-queries/themes-articles';
import { getQuery as getThemesArticlesCount } from '~/groq-queries/themes-articles-count';

const props = defineProps({
  searchEmpty: Boolean,
  searchResults: {
    type: Array,
    default: () => [],
  },
  showMoreButton: Boolean,
  pending: Boolean,
  themeId: {
    type: String,
    default: '',
  },
});

const sanity = useSanity();
const articlesChunkSize = 8;
const offset = ref(0);
const limit = ref(articlesChunkSize);
const isPending = ref(false);
const totalArticles = ref(0);

function fetchArticles() {
  return sanity.fetch(
    getArticlesQuery({
      themeId: props.themeId,
      offset: offset.value,
      limit: limit.value,
    })
  );
}

const { data: initialData } = await useSanityQuery(
  getArticlesQuery({
    themeId: props.themeId,
    offset: offset.value,
    limit: limit.value,
  })
);

// Получите общее количество статей при инициализации
const { data: totalData } = await useSanityQuery(
  getThemesArticlesCount({
    themeId: props.themeId,
  })
);

totalArticles.value = totalData.value;

const showMoreButton = computed(
  () => initialData && initialData.value.length < totalArticles.value
);

async function fetchMoreArticles() {
  useEventTracking('click', 'Show more results');
  offset.value += articlesChunkSize;
  limit.value += articlesChunkSize;

  const { data: moreArticles } = await useAsyncData(
    'moreArticles',
    fetchArticles
  );
  initialData.value = [...initialData.value, ...moreArticles.value];
}
</script>

<template>
  <div class="articles">
    <ul class="articles__list">
      <li v-for="articleItem in initialData" :key="articleItem._id">
        <a
          v-if="articleItem.isArticleExternalSourceType"
          :href="articleItem.externalSource"
          target="_blank"
        >
          <ArticleItem :article="articleItem" />
        </a>
        <NuxtLink
          v-else
          :to="{
            name: 'articles-slug',
            params: { slug: articleItem.slug.current },
          }"
        >
          <ArticleItem :article="articleItem" />
        </NuxtLink>
      </li>
    </ul>
    <div v-show="showMoreButton" class="articles__more-button">
      <MoreContentButton :loading="isPending" @click="fetchMoreArticles" />
    </div>
  </div>
</template>

<style scoped>
.articles {
  &__list {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__more-button {
    display: flex;
    justify-content: center;
    margin-top: 32px;
  }

  @media (--small-vp) {
    &__list {
      gap: 8px;
    }
  }
}
</style>
